import { featureFlags } from '@shared-services/feature-flags';
import { AmountFormat, parseAmountFormat } from './customAmount';
import { CustomPricingSuggestedAmount, ProductType } from './types';

export const CUSTOM_PRICING_MODE = 'CUSTOM';

export interface CustomPricingConfig {
    currency: string;
    /** Decimals a customer amount may have: 0 or 2, resolved per currency by the catalog. */
    decimals: number;
    minimum: number | null;
    displayedMinimum: string | null;
    maximum: number | null;
    displayedMaximum: string | null;
    /** Whether the free-entry field is shown. */
    allowCustomAmount: boolean;
    suggestedAmounts: CustomPricingSuggestedAmount[];
    format: AmountFormat;
}

/**
 * Only a `custom_pricing.mode` of CUSTOM makes the price the customer's: a
 * fixed-price product has no `custom_pricing`, or a null one in the monolith's
 * serialized product view, and any other mode is fixed.
 * With `runtime.ssr.customPricing.enabled` off, a CUSTOM product renders as a
 * fixed-price one, as it did before pay-what-you-want.
 */
export const isCustomPricing = (product?: Partial<ProductType> | null) =>
    product?.custom_pricing?.mode === CUSTOM_PRICING_MODE &&
    featureFlags.getBoolean('runtime.ssr.customPricing.enabled');

export function getCustomPricingConfig(
    product: ProductType
): CustomPricingConfig | null {
    const customPricing = product.custom_pricing;
    if (!customPricing || !isCustomPricing(product)) {
        return null;
    }
    const suggestedAmounts = customPricing.suggested_amounts ?? [];
    return {
        currency: product.currency ?? '',
        decimals: customPricing.decimals === 0 ? 0 : 2,
        minimum: customPricing.minimum ?? null,
        displayedMinimum: customPricing.displayed_minimum ?? null,
        maximum: customPricing.maximum ?? null,
        displayedMaximum: customPricing.displayed_maximum ?? null,
        // With nothing to pick from, the field is the only way to choose.
        allowCustomAmount:
            customPricing.allow_custom_amount?.toString() !== 'false' ||
            suggestedAmounts.length === 0,
        suggestedAmounts,
        format: parseAmountFormat([
            product.displayed_price,
            ...suggestedAmounts.map(({ displayed_amount }) => displayed_amount),
            customPricing.displayed_minimum,
            customPricing.displayed_maximum,
        ]),
    };
}
